/**
 * 教室API
 */
import { Component, Vue } from 'vue-property-decorator'
import { ApiResponseBranchList, ApiResponseBranch, ApiResponseBranchEditData } from '@/models/apiResponses/branches'
import { BranchSearchParamsType, BranchEditType } from '@/models/branchListModel'
import { UserRoles } from '@/const/UserRoles'

@Component
export default class BranchesApi extends Vue {
  /**
   * 教室一覧取得
   * @param role ユーザー権限
   * @param academyId 塾ID
   * @returns 教室一覧情報
   */
  protected async getBranches(role: UserRoles, academyId?: number): Promise<ApiResponseBranch[]> {
    const { data }: { data: ApiResponseBranch[] } = await Vue.prototype.$http.httpWithToken
      .get(`/v2/${role}/branches`, { params: { academyId: academyId } })
      .catch(() => ({ data: [] }))

    return data
  }

  /**
   * 教室一覧検索
   * @param params 検索パラメーター群
   * @param role ユーザー権限
   * @returns ページネーション情報付き教室一覧情報
   */
  protected async searchBranches(params: BranchSearchParamsType, role: UserRoles): Promise<ApiResponseBranchList> {
    const { data }: { data: ApiResponseBranchList } = await Vue.prototype.$http.httpWithToken
      .get(`/v2/${role}/branches/search`, { params: params })
      .catch(() => ({ data: { responses: [], pagination: null } }))

    return data
  }

  /**
   * 教室情報取得
   * @param id 教室ID
   * @param role ユーザー権限
   * @returns 教室情報
   */
  protected async getBranchData(id: number, role: UserRoles): Promise<ApiResponseBranchEditData> {
    const { data }: { data: ApiResponseBranchEditData } = await Vue.prototype.$http.httpWithToken.get(
      `/v2/${role}/branches/${id}`
    )

    return data
  }

  /**
   * 教室情報登録
   * @param editData 教室編集情報
   * @param role ユーザー権限
   */
  protected async createBranchData(editData: BranchEditType, role: UserRoles): Promise<void> {
    await Vue.prototype.$http.httpWithToken.post(`/v2/${role}/branches`, {
      // string型で受け渡されるのでnumberに変換
      academyId: Number(editData.academyId),
      branchName: editData.branchName,
      branchCode: editData.branchCode,
    })
  }

  /**
   * 教室情報更新
   * @param editData 教室更新情報
   * @param role ユーザー権限
   */
  protected async updateBranchData(editData: BranchEditType, role: UserRoles): Promise<void> {
    await Vue.prototype.$http.httpWithToken.put(`/v2/${role}/branches/${editData.id}`, {
      branchName: editData.branchName,
    })
  }

  /**
   * 教室情報削除
   * @param id 教室ID
   * @param role ユーザー権限
   */
  protected async deleteBranchData(id: number, role: UserRoles): Promise<void> {
    await Vue.prototype.$http.httpWithToken.delete(`/v2/${role}/branches/${id}`)
  }

  /**
   * 教室一覧のエクセルダウンロード
   * @param params 検索パラメーター群
   * @param branchIds ダウンロード対象の教室ID
   * @param role ユーザー権限
   * @returns エクセルデータ
   */
  protected async exportBranchExcel(
    params: BranchSearchParamsType,
    branchIds: number[],
    role: UserRoles
  ): Promise<Blob> {
    const { data }: { data: Blob } = await Vue.prototype.$http.httpWithToken.get(`/v2/${role}/branches/export`, {
      params: Object.assign(params, { branchIds: branchIds }),
      responseType: 'blob',
      dataType: 'binary',
    })

    return data
  }

  /**
   * 教室一覧エクセルアップロード
   * @param file エクセルファイル
   * @param role ユーザー権限
   */
  protected async importBranchExcel(file: File, role: UserRoles): Promise<void> {
    const headers = { 'Content-Type': 'multipart/form-data' }
    const params = new FormData()
    params.append('excel', file)
    await Vue.prototype.$http.httpWithToken.post(`/v2/${role}/branches/import`, params, {
      headers: headers,
    })
  }
}
